import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useMemo } from 'react';

import { trpc, type StockRemainingByDate } from '@/lib/trpc';

type BreadOptionMapping = NonNullable<StockRemainingByDate>['breadOptionsByMenu'][string][number];

type UseStockRemainingReturn = {
  breadOptionsByMenu: Record<string, BreadOptionMapping[]>;
  breadStockMap: Map<number, number>;
  error: unknown;
  isBreadMap: Map<number, boolean>;
  isLoading: boolean;
  isMenuOutOfStock: (menuId: number) => boolean;
  menuStockMap: Map<number, number>;
  refresh: () => void;
};

export function useStockRemaining(date: Date | null): UseStockRemainingReturn {
  const dateStr = date ? format(date, 'yyyy-MM-dd') : null;

  const {
    data,
    error,
    isLoading,
    refetch,
  } = useQuery(
    trpc.stock.getRemainingByDate.queryOptions(
      { date: dateStr ?? '' },
      {
        enabled: !!dateStr,
      }
    )
  );

  const menuStockMap = useMemo(() => {
    return new Map((data?.menuStock ?? []).map((item) => [item.menuId, item.remaining]));
  }, [data]);

  const breadStockMap = useMemo(() => {
    return new Map((data?.breadStock ?? []).map((item) => [item.breadId, item.remaining]));
  }, [data]);

  const isBreadMap = useMemo(() => {
    return new Map((data?.menuStock ?? []).map((item) => [item.menuId, item.isBread]));
  }, [data]);

  const breadOptionsByMenu = useMemo(() => {
    return data?.breadOptionsByMenu ?? {};
  }, [data]);

  const isMenuOutOfStock = (menuId: number) => {
    const menuRemaining = menuStockMap.get(menuId);
    if (menuRemaining !== undefined && menuRemaining <= 0) {
      return true;
    }

    const breadOptions = breadOptionsByMenu[String(menuId)] ?? [];
    if (breadOptions.length === 0) {
      return false;
    }

    return breadOptions.every((option) => (breadStockMap.get(option.breadId) ?? 0) <= 0);
  };

  return {
    breadOptionsByMenu,
    breadStockMap,
    error,
    isBreadMap,
    isLoading,
    isMenuOutOfStock,
    menuStockMap,
    refresh: () => {
      void refetch();
    },
  };
}
